import React, { useState, useEffect } from 'react'
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Alert,
  CircularProgress,
  Grid,
  Card,
  CardContent
} from '@mui/material'
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Login as LoginIcon
} from '@mui/icons-material'
import { API_BASE_URL } from '../config/api'
import { useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '@/hooks/redux'
import { login } from '@/store/slices/authSlice'

interface Centre {
  id: string
  name: string
  code: string
  description: string
  location: {
    country: string
    region: string
    district: string
  } | null
  contact_email: string
  contact_phone: string
  address: string
  status: string
  created_at: string
  updated_at: string
}

interface CentreStats {
  total_farmers: number
  total_admins: number
  total_auction_lots: number
  total_products: number
  total_orders: number
  total_revenue: number
}

const Centres: React.FC = () => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { user } = useAppSelector(state => state.auth)
  const [centres, setCentres] = useState<Centre[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [openDialog, setOpenDialog] = useState(false)
  const [openStatsDialog, setOpenStatsDialog] = useState(false)
  const [selectedCentre, setSelectedCentre] = useState<Centre | null>(null)
  const [stats, setStats] = useState<CentreStats | null>(null)
  const [loginLoading, setLoginLoading] = useState<string | null>(null)
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: '',
    contact_email: '',
    contact_phone: '',
    address: '',
    country: 'Zimbabwe',
    region: '',
    district: '',
    status: 'active'
  })

  useEffect(() => {
    fetchCentres()
  }, [])

  const fetchCentres = async () => {
    try {
      setLoading(true)
      const token = localStorage.getItem('token')
      const response = await fetch(`${API_BASE_URL}/centres`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      const data = await response.json()
      
      if (data.success) {
        setCentres(data.data)
        setError('')
      } else {
        setError(data.error?.message || 'Failed to load centres')
      }
    } catch (err) {
      setError('Failed to load centres')
      console.error('Error fetching centres:', err)
    } finally {
      setLoading(false)
    }
  }

  const fetchCentreStats = async (centreId: string) => {
    try {
      const token = localStorage.getItem('token')
      const response = await fetch(`${API_BASE_URL}/centres/${centreId}/statistics`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      const data = await response.json()
      
      if (data.success) {
        setStats(data.data)
        setOpenStatsDialog(true)
      } else {
        setError(data.error?.message || 'Failed to load statistics')
      }
    } catch (err) {
      setError('Failed to load statistics')
      console.error('Error fetching stats:', err)
    }
  }

  const handleOpenDialog = (centre?: Centre) => {
    if (centre) {
      setSelectedCentre(centre)
      setFormData({
        name: centre.name,
        code: centre.code,
        description: centre.description || '',
        contact_email: centre.contact_email || '',
        contact_phone: centre.contact_phone || '',
        address: centre.address || '',
        country: centre.location?.country || 'Zimbabwe',
        region: centre.location?.region || '',
        district: centre.location?.district || '',
        status: centre.status
      })
    } else {
      setSelectedCentre(null)
      setFormData({
        name: '',
        code: '',
        description: '',
        contact_email: '',
        contact_phone: '',
        address: '',
        country: 'Zimbabwe',
        region: '',
        district: '',
        status: 'active'
      })
    }
    setOpenDialog(true)
  }

  const handleCloseDialog = () => {
    setOpenDialog(false)
    setSelectedCentre(null)
    setError('')
    setSuccess('')
  }

  const handleSubmit = async () => {
    try {
      const token = localStorage.getItem('token')
      const url = selectedCentre 
        ? `${API_BASE_URL}/centres/${selectedCentre.id}`
        : `${API_BASE_URL}/centres`
      
      const body = {
        name: formData.name,
        code: formData.code.toUpperCase(),
        description: formData.description,
        contact_email: formData.contact_email,
        contact_phone: formData.contact_phone,
        address: formData.address,
        location: {
          country: formData.country,
          region: formData.region,
          district: formData.district
        },
        status: formData.status
      }

      const response = await fetch(url, {
        method: selectedCentre ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      })

      const data = await response.json()

      if (data.success) {
        setSuccess(selectedCentre ? 'Centre updated successfully' : 'Centre created successfully')
        handleCloseDialog()
        fetchCentres()
      } else {
        setError(data.error?.message || 'Failed to save centre')
      }
    } catch (err) {
      setError('Failed to save centre')
      console.error('Error saving centre:', err)
    }
  }

  const handleLoginToCentre = async (centre: Centre) => {
    try {
      setLoginLoading(centre.id)
      setError('')
      
      const token = localStorage.getItem('token')
      const response = await fetch(`${API_BASE_URL}/centres/${centre.id}/login`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })
      
      const data = await response.json()
      
      if (data.success) {
        // Update auth state with new token and user context
        dispatch(login({
          user: data.data.user,
          token: data.data.token
        }))
        
        // Store in localStorage
        localStorage.setItem('token', data.data.token)
        localStorage.setItem('user', JSON.stringify(data.data.user))
        
        setSuccess(`Logged in to ${centre.name} as centre admin`)
        
        // Redirect to dashboard after a short delay
        setTimeout(() => {
          navigate('/dashboard')
          window.location.reload() // Reload to ensure all components update
        }, 1000)
      } else {
        setError(data.error?.message || 'Failed to login to centre')
      }
    } catch (err: any) {
      setError(err.message || 'Failed to login to centre')
    } finally {
      setLoginLoading(null)
    }
  }

  const handleDelete = async (centreId: string) => {
    if (!window.confirm('Are you sure you want to delete this centre? This action cannot be undone.')) {
      return
    }

    try {
      const token = localStorage.getItem('token')
      const response = await fetch(`${API_BASE_URL}/centres/${centreId}`, {
        method: 'DELETE',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      const data = await response.json()

      if (data.success) {
        setSuccess('Centre deleted successfully')
        setTimeout(() => fetchCentres(), 500)
      } else {
        setError(data.error?.message || 'Failed to delete centre')
      }
    } catch (err) {
      setError('Failed to delete centre')
      console.error('Error deleting centre:', err)
    }
  }

  const handleToggleStatus = async (centreId: string, currentStatus: string) => {
    const newStatus = currentStatus === 'active' ? 'inactive' : 'active'
    
    try {
      const token = localStorage.getItem('token')
      const response = await fetch(`${API_BASE_URL}/centres/${centreId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      })

      const data = await response.json()

      if (data.success) {
        setSuccess(`Centre ${newStatus === 'active' ? 'activated' : 'deactivated'} successfully`)
        fetchCentres()
      } else {
        setError(data.error?.message || 'Failed to update centre status')
      }
    } catch (err) {
      setError('Failed to update centre status')
      console.error('Error updating centre status:', err)
    }
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Centre Management
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Centre
        </Button>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Code</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Location</TableCell>
              <TableCell>Contact</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {centres.map((centre) => (
              <TableRow key={centre.id}>
                <TableCell>
                  <Chip label={centre.code} color="primary" size="small" />
                </TableCell>
                <TableCell>{centre.name}</TableCell>
                <TableCell>
                  {centre.location 
                    ? `${centre.location.district}, ${centre.location.region}`
                    : 'N/A'}
                </TableCell>
                <TableCell>
                  {centre.contact_email || centre.contact_phone || 'N/A'}
                </TableCell>
                <TableCell>
                  <Chip 
                    label={centre.status} 
                    color={centre.status === 'active' ? 'success' : 'warning'}
                    size="small"
                  />
                </TableCell>
                <TableCell align="right">
                  <Box display="flex" gap={1} justifyContent="flex-end" alignItems="center">
                    {user?.role === 'superadmin' && (
                      <Button
                        size="small"
                        variant="contained"
                        color="primary"
                        startIcon={loginLoading === centre.id ? <CircularProgress size={16} color="inherit" /> : <LoginIcon />}
                        onClick={() => handleLoginToCentre(centre)}
                        disabled={loginLoading === centre.id}
                        sx={{ minWidth: '140px' }}
                      >
                        {loginLoading === centre.id ? 'Logging in...' : 'Login To Center'}
                      </Button>
                    )}
                    <IconButton
                      size="small"
                      onClick={() => fetchCentreStats(centre.id)}
                      title="View Statistics"
                    >
                      <ViewIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDialog(centre)}
                      title="Edit"
                    >
                      <EditIcon />
                    </IconButton>
                    <Button
                      size="small"
                      variant="outlined"
                      color={centre.status === 'active' ? 'warning' : 'success'}
                      onClick={() => handleToggleStatus(centre.id, centre.status)}
                      sx={{ mr: 1, minWidth: '90px' }}
                    >
                      {centre.status === 'active' ? 'Deactivate' : 'Activate'}
                    </Button>
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(centre.id)}
                      title="Delete"
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedCentre ? 'Edit Centre' : 'Create New Centre'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Centre Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Centre Code"
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                required
                inputProps={{ maxLength: 10 }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Country"
                value={formData.country}
                onChange={(e) => setFormData({ ...formData, country: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Region"
                value={formData.region}
                onChange={(e) => setFormData({ ...formData, region: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="District"
                value={formData.district}
                onChange={(e) => setFormData({ ...formData, district: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Contact Email"
                type="email"
                value={formData.contact_email}
                onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Contact Phone"
                value={formData.contact_phone}
                onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                multiline
                rows={2}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            {selectedCentre ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Statistics Dialog */}
      <Dialog open={openStatsDialog} onClose={() => setOpenStatsDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Centre Statistics</DialogTitle>
        <DialogContent>
          {stats && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={6}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      Farmers
                    </Typography>
                    <Typography variant="h4">
                      {stats.total_farmers}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      Admins
                    </Typography>
                    <Typography variant="h4">
                      {stats.total_admins}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      Auction Lots
                    </Typography>
                    <Typography variant="h4">
                      {stats.total_auction_lots}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      Products
                    </Typography>
                    <Typography variant="h4">
                      {stats.total_products}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      Orders
                    </Typography>
                    <Typography variant="h4">
                      {stats.total_orders}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      Revenue
                    </Typography>
                    <Typography variant="h4">
                      ${typeof stats.total_revenue === 'number' ? stats.total_revenue.toFixed(2) : parseFloat(stats.total_revenue || '0').toFixed(2)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenStatsDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default Centres
